// Sprint CRM-4 — Dashboard del CRM: numeri dal server (/crm/dashboard), solo opportunità visibili.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bar, BarChart, LabelList, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useSchermoLargo } from "@/lib/useSchermo";
import { useCrmMe, useAziendaCrm, CampoAzienda, TornaIndietro, TitoloCrm, apriPipeline, errore, STATI_OPP } from "./crmComuni";

const TUTTI_STATI = ["aperta", "vinta", "persa", "abbandonata"];
const CHIUSI = ["vinta", "persa", "abbandonata"];
const PERIODI = { mese: "Mese corrente", 30: "Ultimi 30 giorni", prec: "Mese precedente", libero: "Dal… al…" };
const BLU = "#0F4C81";
const lsChiave = (cid) => `crm-dashboard-${cid}`;
const SCELTE_VUOTE = { pipeline: "_tutte", utente: "tutti", periodo: "mese", dal: "", al: "" };
export const leggiScelte = (cid) => { try { return { ...SCELTE_VUOTE, ...JSON.parse(localStorage.getItem(lsChiave(cid)) || "{}") }; } catch { return SCELTE_VUOTE; } };

// Collegamento alla vista a righe con i filtri esistenti; null se non c'è una pipeline
export function linkRighe(pid, filtri, utente) {
  if (!pid) return null;
  const f = { ...filtri };
  if (utente && utente !== "tutti") f.titolari = [utente];
  return `/crm?pipeline=${pid}&vista=righe&filtri=${encodeURIComponent(JSON.stringify(f))}`;
}

export function testoFontePrincipale(fp) {
  if (!fp) return { testo: "—", tutte: null };
  const { nomi, n } = fp;
  if (nomi.length === 1) return { testo: `${nomi[0]} (${n})`, tutte: null };
  const testa = nomi.length > 3 ? `${nomi.slice(0, 2).join(", ")} e altre ${nomi.length - 2}` : nomi.join(", ");
  return { testo: `${testa} (${n} ciascuna)`, tutte: nomi.join(", ") };
}

export function datiFunnel(funnel, periodo, pid, utente) {
  return funnel.map((f) => {
    const chiusura = f.tipo !== "normale";
    const filtri = chiusura ? { stati: ["vinta"], fasi: [f.fase_id], chiusa_dal: periodo.dal, chiusa_al: periodo.al }
      : { stati: ["aperta"], fasi: [f.fase_id] };
    return { id: f.fase_id, nome: f.nome, valore: f.n, link: linkRighe(pid, filtri, utente),
      testo: `${f.n}${chiusura ? " vinte nel periodo" : ""}${f.oltre ? ` · ${f.oltre} oltre l'avviso` : ""}` };
  });
}

export const datiTempi = (tempi) => tempi.map((t) => ({ id: t.fase_id, nome: t.nome, valore: t.giorni ?? 0,
  testo: t.giorni == null ? "—" : `${String(t.giorni).replace(".", ",")} giorni` }));

function Num({ n, to, cid, pid, testid, suffisso = "" }) {
  if (n == null) return <span data-testid={testid}>—</span>;
  if (!to) return <span data-testid={testid}>{n}{suffisso}</span>;
  return <Link to={to} onClick={() => apriPipeline(cid, pid)} className="hover:underline text-[#0F4C81]" data-testid={testid}>{n}{suffisso}</Link>;
}

export const Box = ({ children, testid, titolo, nota }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
    {titolo && <div className="flex items-baseline justify-between mb-3">
      <h2 className="font-heading text-base font-semibold text-slate-900">{titolo}</h2>
      {nota && <span className="text-xs text-slate-500">{nota}</span>}</div>}
    {children}
  </section>
);

export function Riquadro({ titolo, children, sotto, testid }) {
  return (
    <Box testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{titolo}</div>
      <div className="font-heading text-3xl font-semibold text-slate-900 mt-1 tabular-nums">{children}</div>
      {sotto && <div className="text-xs text-slate-500 mt-1">{sotto}</div>}
    </Box>
  );
}

function Riquadri({ d, cid, pid, utente }) {
  const p = d.periodo;
  const L = (filtri) => linkRighe(pid, filtri, utente);
  const creata = { creata_dal: p.dal, creata_al: p.al };
  const chiusa = { chiusa_dal: p.dal, chiusa_al: p.al };
  const fp = testoFontePrincipale(d.fonte_principale);
  const unica = d.fonte_principale?.nomi.length === 1 ? d.per_fonte.find((x) => x.id && x.nome === d.fonte_principale.nomi[0]) : null;
  const n = { cid, pid };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
      <Riquadro testid="crm-dash-in-corso" titolo="In corso"><Num {...n} n={d.in_corso} to={L({ stati: ["aperta"] })} testid="crm-dash-in-corso-n" /></Riquadro>
      <Riquadro testid="crm-dash-inserite" titolo="Inserite nel periodo"
        sotto={<span className="space-x-1">{TUTTI_STATI.map((s, i) => <span key={s}>{i ? "· " : ""}
          <Num {...n} n={d.inserite[s]} to={L({ stati: [s], ...creata })} testid={`crm-dash-inserite-${s}`} /> {STATI_OPP[s].toLowerCase()}</span>)}</span>}>
        <Num {...n} n={d.inserite.inserite} to={L({ stati: TUTTI_STATI, ...creata })} testid="crm-dash-inserite-n" /></Riquadro>
      <Riquadro testid="crm-dash-chiuse" titolo="Chiuse nel periodo"
        sotto={<span className="space-x-1">{CHIUSI.map((s, i) => <span key={s}>{i ? "· " : ""}
          <Num {...n} n={d.chiuse[s]} to={L({ stati: [s], ...chiusa })} testid={`crm-dash-chiuse-${s}`} /> {STATI_OPP[s].toLowerCase()}</span>)}</span>}>
        <Num {...n} n={d.chiuse.totale} to={L({ stati: CHIUSI, ...chiusa })} testid="crm-dash-chiuse-n" /></Riquadro>
      <Riquadro testid="crm-dash-conversione" titolo="Conversione"
        sotto={<span data-testid="crm-dash-conversione-su"><Num {...n} n={d.conversione.vinte} to={L({ stati: ["vinta"], ...creata })} testid="crm-dash-conversione-vinte" /> su {d.conversione.su} inserite</span>}>
        <span data-testid="crm-dash-conversione-n">{d.conversione.percentuale == null ? "—" : `${d.conversione.percentuale}%`}</span></Riquadro>
      <Riquadro testid="crm-dash-fonte" titolo="Fonte principale">
        <span className="text-xl" title={fp.tutte || undefined} data-testid="crm-dash-fonte-n">
          {unica ? <Num {...n} n={fp.testo} to={L({ stati: TUTTI_STATI, fonti: [unica.id], ...creata })} testid="crm-dash-fonte-link" /> : fp.testo}</span></Riquadro>
    </div>
  );
}

function Barre({ dati, testid, onClick }) {
  const w = useSchermoLargo(640) ? 170 : 110;  // Sprint CRM-6 — da telefono nomi e numeri più stretti
  return (
    <div data-testid={testid} style={{ height: dati.length * 38 + 16 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={dati} layout="vertical" margin={{ top: 4, right: w, bottom: 4, left: 8 }}>
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis type="category" dataKey="nome" width={w} tickLine={false} axisLine={false} tick={{ fontSize: 13, fill: "#334155" }} />
          <Bar dataKey="valore" fill={BLU} radius={[0, 4, 4, 0]} isAnimationActive={false} barSize={20}
            cursor={onClick ? "pointer" : undefined} onClick={onClick ? (x) => onClick(x?.payload || x) : undefined}>
            <LabelList dataKey="testo" position="right" style={{ fontSize: 12, fill: "#0f172a" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const TH = ({ children, left }) => <th className={`${left ? "text-left" : ""} py-1.5 font-medium`}>{children}</th>;
const pc = (x) => (x == null ? "—" : `${x}%`);

export function TabellaStati({ righe, prefisso, link, n, intestazione, conInCorso }) {
  return (
    <div className="overflow-x-auto">{/* Sprint CRM-6 — da telefono la tabella scorre di lato */}
    <table className="w-full text-sm min-w-[480px]">
      <thead><tr className="text-[11px] uppercase text-slate-500 text-right">
        <TH left>{intestazione}</TH><TH>Inserite</TH>{conInCorso && <TH>In corso</TH>}<TH>Vinte</TH>
        {conInCorso && <><TH>Perse</TH><TH>Abbandonate</TH></>}<TH>Conversione</TH></tr></thead>
      <tbody>{righe.map((r) => {
        const k = r.id || "_nessuno";
        const cella = (stati, val, t) => <td><Num {...n} pid={link(r, stati).pid} n={val} to={link(r, stati).to} testid={`${prefisso}-${k}-${t}`} /></td>;
        return (
          <tr key={k} className="border-t border-slate-100 text-right tabular-nums" data-testid={`${prefisso}-${k}`}>
            <td className="text-left py-2.5">{r.nome}{r.archiviata ? " (archiviata)" : ""}</td>
            {cella(TUTTI_STATI, r.inserite, "inserite")}{conInCorso && cella(["aperta"], r.aperta, "aperta")}
            {cella(["vinta"], r.vinta, "vinta")}
            {conInCorso && <>{cella(["persa"], r.persa, "persa")}{cella(["abbandonata"], r.abbandonata, "abbandonata")}</>}
            <td data-testid={`${prefisso}-${k}-conversione`}>{pc(r.conversione)}</td>
          </tr>);
      })}</tbody>
    </table>
    </div>
  );
}

function Selettori({ d, s, set }) {
  return (
    <div className="flex items-end gap-3 flex-wrap" data-testid="crm-dash-selettori">
      <div><Label className="text-xs text-slate-500">Pipeline</Label>
        <Select value={s.pipeline} onValueChange={(v) => set({ pipeline: v })}>
          <SelectTrigger className="w-56" data-testid="crm-dash-pipeline"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="_tutte">Tutte</SelectItem>{d.pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
        </Select></div>
      {d.mostra_utente && <div><Label className="text-xs text-slate-500">Utente</Label>
        <Select value={s.utente} onValueChange={(v) => set({ utente: v })}>
          <SelectTrigger className="w-56" data-testid="crm-dash-utente"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutti">Tutti</SelectItem>
            {d.nessun_titolare && <SelectItem value="_nessuno">Nessun titolare</SelectItem>}
            {d.titolari.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}</SelectContent>
        </Select></div>}
      <div><Label className="text-xs text-slate-500">Periodo</Label>
        <Select value={s.periodo} onValueChange={(v) => set({ periodo: v })}>
          <SelectTrigger className="w-48" data-testid="crm-dash-periodo"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PERIODI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select></div>
      {s.periodo === "libero" && <>
        <Input type="date" className="w-40" value={s.dal} onChange={(e) => set({ dal: e.target.value })} data-testid="crm-dash-dal" />
        <Input type="date" className="w-40" value={s.al} onChange={(e) => set({ al: e.target.value })} data-testid="crm-dash-al" />
      </>}
      <span className="text-xs text-slate-500 pb-2" data-testid="crm-dash-periodo-testo">
        dal {d.periodo.dal.split("-").reverse().join("/")} al {d.periodo.al.split("-").reverse().join("/")}</span>
    </div>
  );
}

function Contenuto({ d, cid, s }) {
  const navigate = useNavigate();
  const pid = s.pipeline === "_tutte" ? null : s.pipeline;
  const utente = d.mostra_utente ? s.utente : null;
  const n = { cid, pid };
  const creata = { creata_dal: d.periodo.dal, creata_al: d.periodo.al };
  const vai = (x) => { if (x.link) { apriPipeline(cid, pid); navigate(x.link); } };
  const linkFonte = (r, stati) => ({ pid, to: linkRighe(pid, { stati, fonti: [r.id || "_nessuno"], ...creata }, utente) });  // CRM-5: anche «Senza fonte»
  const chiusa = { chiusa_dal: d.periodo.dal, chiusa_al: d.periodo.al };
  const linkUtente = (r, stati) => ({ pid, to: linkRighe(pid, { stati, ...creata }, r.id || "_nessuno") });
  const linkPipe = (r, stati) => ({ pid: r.id, to: linkRighe(r.id, { stati, ...creata }, utente) });
  return (
    <div className="space-y-4">
      <Riquadri d={d} cid={cid} pid={pid} utente={utente} />
      {d.funnel && <Box testid="crm-dash-funnel" titolo="Opportunità per fase" nota="In corso oggi; nelle fasi di chiusura le vinte del periodo">
        <Barre dati={datiFunnel(d.funnel, d.periodo, pid, utente)} testid="crm-dash-funnel-barre" onClick={vai} />
        {/* Sprint CRM-5 — «oltre l'avviso» cliccabile: filtro «Oltre l'avviso della fase» + la fase */}
        {d.funnel.some((f) => f.oltre) && <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-2" data-testid="crm-dash-oltre">
          {d.funnel.filter((f) => f.oltre).map((f) => <span key={f.fase_id}>{f.nome}: <Num {...n} n={f.oltre} suffisso=" oltre l'avviso"
            to={linkRighe(pid, { stati: f.tipo === "normale" ? ["aperta"] : ["vinta"], fasi: [f.fase_id], oltre_avviso: "si" }, utente)}
            testid={`crm-dash-oltre-${f.fase_id}`} /></span>)}</div>}</Box>}
      <Box testid="crm-dash-per-fonte" titolo="Per fonte" nota="Inserite nel periodo">
        <TabellaStati righe={d.per_fonte} prefisso="crm-dash-fonte" link={linkFonte} n={n} intestazione="Fonte" /></Box>
      {d.per_utente && <Box testid="crm-dash-per-utente" titolo="Per utente" nota="Inserite nel periodo, stato di oggi">
        <TabellaStati righe={d.per_utente} prefisso="crm-dash-utente" link={linkUtente} n={n} intestazione="Titolare" conInCorso /></Box>}
      {d.per_pipeline && <Box testid="crm-dash-per-pipeline" titolo="Per pipeline" nota="Inserite nel periodo, stato di oggi">
        <TabellaStati righe={d.per_pipeline} prefisso="crm-dash-pipe" link={linkPipe} n={n} intestazione="Pipeline" conInCorso /></Box>}
      <Box testid="crm-dash-motivi" titolo="Motivi di perdita" nota="Perse nel periodo">
        {d.motivi.length === 0 ? <div className="text-sm text-slate-500" data-testid="crm-dash-motivi-vuoto">—</div> : (
          <table className="w-full text-sm"><tbody>{d.motivi.map((m) => (
            <tr key={m.id || "_senza"} className="border-t first:border-t-0 border-slate-100 tabular-nums" data-testid={`crm-dash-motivo-${m.id || "senza"}`}>
              <td className="py-2">{m.nome}</td><td className="text-right"><Num {...n} n={m.n} testid={`crm-dash-motivo-${m.id || "senza"}-n`}
                to={linkRighe(pid, { stati: ["persa"], motivi: [m.id || "_nessuno"], ...chiusa }, utente)} /></td></tr>))}</tbody></table>)}
      </Box>
      {d.tempi && <Box testid="crm-dash-tempi" titolo="Tempo medio per fase" nota="Giorni, sulle opportunità uscite dalla fase nel periodo">
        <Barre dati={datiTempi(d.tempi)} testid="crm-dash-tempi-barre" /></Box>}
    </div>
  );
}

export default function CrmDashboard() {
  const crmMe = useCrmMe();
  const [cid, setCid, aziende] = useAziendaCrm(crmMe);
  const [s, setS] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => { if (cid) setS(leggiScelte(cid)); }, [cid]);
  const set = (x) => { const n = { ...s, ...x }; setS(n); try { localStorage.setItem(lsChiave(cid), JSON.stringify(n)); } catch {} };
  useEffect(() => {
    if (!cid || !s || (s.periodo === "libero" && !(s.dal && s.al))) return;
    const p = new URLSearchParams({ company_id: cid, periodo: s.periodo, utente: s.utente });
    if (s.pipeline !== "_tutte") p.set("pipeline_id", s.pipeline);
    if (s.periodo === "libero") { p.set("dal", s.dal); p.set("al", s.al); }
    api.get(`/crm/dashboard?${p}`).then((r) => setD(r.data)).catch((e) => {
      if (e?.response?.status === 404 && s.pipeline !== "_tutte") set({ pipeline: "_tutte" }); else toast.error(errore(e));
    });
  }, [cid, s]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4" data-testid="crm-dashboard-page">
      <TornaIndietro to="/crm" testo="Torna al CRM" testid="crm-dashboard-indietro" />
      <div className="flex items-end gap-x-3 gap-y-2 flex-wrap">
        <TitoloCrm sotto="Dashboard" />
        <CampoAzienda aziende={aziende} cid={cid} onScegli={(id) => { setCid(id); setS(null); setD(null); }} />
      </div>
      {d && s && <Selettori d={d} s={s} set={set} />}
      {d && s && <Contenuto d={d} cid={cid} s={s} />}
    </div>
  );
}
